import { useMemo } from "react";
import type { LabSearchVisibility, LabService, RadiologyService } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { EMPTY_ADDRESS, type AddressFormValues } from "@/components/profile/AddressFormFields";
import { LabTab } from "@/components/profile/tabs/LabTab";
import { RadiologyTab } from "@/components/profile/tabs/RadiologyTab";
import { usePermissao } from "@/lib/use-permissao";
import { CatalogoPublicoConfig } from "@/components/catalogo/publico/CatalogoPublicoConfig";

type EnderecoBruto = {
  zipCode?: string | null;
  address?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
};

function paraFormulario(e: EnderecoBruto | null | undefined): AddressFormValues {
  if (!e) return EMPTY_ADDRESS;
  return {
    zipCode: e.zipCode ?? "",
    address: e.address ?? "",
    addressNumber: e.addressNumber ?? "",
    addressComplement: e.addressComplement ?? "",
    neighborhood: e.neighborhood ?? "",
    city: e.city ?? "",
    state: e.state ?? "",
  };
}

/** Perfil público do laboratório: nome, logo, serviços, endereço, visibilidade na busca e o link
 *  público do catálogo. O prazo fica em Dados (PrazoDeProducaoSection). */
export function LaboratorioSecao() {
  const utils = trpc.useUtils();
  const podeEditar = usePermissao("settings.manage");
  const perfil = trpc.organization.getProfiles.useQuery();
  const lab = perfil.data?.labProfile;
  const inicial = useMemo(
    () => ({
      name: lab?.name ?? "",
      services: (lab?.services ?? []) as LabService[],
      logoUrl: lab?.logoUrl ?? null,
      addressSame: lab?.addressSame ?? true,
      address: paraFormulario(lab),
      searchVisibility: (lab?.searchVisibility ?? "PUBLIC") as LabSearchVisibility,
    }),
    [lab],
  );
  if (perfil.isLoading) return null;
  return (
    <div className="space-y-6 px-6 py-5">
      <LabTab initial={inicial} onSaved={() => (utils.organization.getProfiles.invalidate(), utils.organization.listMine.invalidate())} />
      <CatalogoPublicoConfig podeEditar={podeEditar} />
    </div>
  );
}

/** Perfil do centro de radiologia: serviços, logo e endereço de atendimento. */
export function RadiologiaSecao() {
  const utils = trpc.useUtils();
  const perfil = trpc.organization.getProfiles.useQuery();
  const rad = perfil.data?.radiologyProfile;
  const inicial = useMemo(
    () => ({
      services: (rad?.services ?? []) as RadiologyService[],
      logoUrl: rad?.logoUrl ?? null,
      addressSame: rad?.addressSame ?? true,
      address: paraFormulario(rad),
    }),
    [rad],
  );
  if (perfil.isLoading) return null;
  return (
    <div className="px-6 py-5">
      <RadiologyTab initial={inicial} onSaved={() => (utils.organization.getProfiles.invalidate(), utils.organization.listMine.invalidate())} />
    </div>
  );
}
